import { FC } from 'react';
import styled from '@emotion/styled';
import { useDispatch, useSelector } from 'react-redux';
import { mediaQuery, Spacings } from '@styles/index';
import { useIsMobile } from '@stores/AppCommonData';
import H3 from '@base/H3/H3';
import P1 from '@base/P1/P1';
import Thumbnail from '@base/Thumbnail/Thumbnail';
import Link from '@base/Link/Link';
import P2 from '@base/P2/P2';
import Image from '@base/Image';
import boxShadowGenerator from '@utils/boxShadowGenerator';
import EducatorDetailedCard from '@comp/EducatorDetailedCard/EducatorDetailedCard';
import { segment } from '@actions/analytics';
import useLevelDisplayInfo from '@api/hooks/profile/useLevelDisplayInfo';
import { SEE_ALL_CLICKED, GOAL_PAGE } from '@constants/segment';
import useGoalEventProps from '@hooks/useGoalEventProps';
import CardCarousel from './CardCarousel';

const SecWrapper = styled.section`
  position: relative;
  margin: auto;
  margin-top: 195px;
  padding-bottom: 32px;
  width: 1376px;
  overflow: hidden;
  @media (max-width: 70.95em) {
    width: 728px;
    margin-top: 96px;
  }
  @media (max-width: 47.95em) {
    width: 100%;
    margin: 88px 0 0;
    padding-bottom: 0;
  }
`;

const CardContainer = styled.div`
  width: 480px;
  height: 228px;
  margin-right: 24px;
  cursor: pointer;
  position: relative;
  display: flex;
  @media (max-width: 70.95em) {
    width: 272px;
    height: 300px;
    margin-top: 32px;
  }
  @media (max-width: 47.95em) {
    margin-right: 16px;
  }
`;

const CardWrapper = styled.div`
  width: 440px;
  height: inherit;
  padding: 24px;
  box-shadow: ${boxShadowGenerator()};
  border-radius: 8px;
  margin-left: 40px;
  position: relative;
  padding-left: 124px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  background-color: var(--color-base-1);
  @media (max-width: 70.95em) {
    width: 272px;
    padding: 95px 16px 24px 16px;
    margin-left: 0;
    box-shadow: ${boxShadowGenerator()};
  }
`;

const CardThumbnailWrapper = styled.div`
  position: absolute;
  overflow: hidden;
  border-radius: 8px;
  ${mediaQuery.aboveTablet} {
    width: 140px;
    height: 180px;
    left: ${Spacings.SPACING_0B};
    top: ${Spacings.SPACING_6B};
  }
  ${mediaQuery.uptoTablet} {
    width: 103px;
    height: 103px;
    top: -${Spacings.SPACING_8B};
    left: ${Spacings.SPACING_4B};
    box-shadow: none;
  }
`;

const EducatorLevelContainer = styled.div`
  position: absolute;
  background-color: ${(props) => props.color};
  z-index: 1;
  border-radius: 50%;
  display: flex;
  justify-content: center;
  align-items: center;
  ${mediaQuery.aboveTablet} {
    width: 90px;
    height: 90px;
    transform: translate(-50%, 150%);
  }
  ${mediaQuery.uptoTablet} {
    width: 66px;
    height: 66px;
    transform: translate(-50%, 105%);
    box-shadow: none;
  }
`;

const EducatorLevelBadgeContainer = styled.div`
  ${mediaQuery.aboveTablet} {
    transform: translate(90%, -60%);
  }
  ${mediaQuery.uptoTablet} {
    transform: translate(90%, -55%);
  }
`;

const CardThumbnail = styled(Thumbnail)`
  &.thumbnail {
    position: absolute;
    z-index: 1;
    background-size: cover;
    object-fit: cover;
    border-radius: 8px;
    ${mediaQuery.aboveTablet} {
      width: 140px;
      height: 180px;
      box-shadow: ${boxShadowGenerator()};
    }
    ${mediaQuery.uptoTablet} {
      width: 103px;
      height: 103px;
      box-shadow: none;
    }
  }
`;

const TopContent = styled.div`
  display: flex;
  flex-direction: column;
`;

const Description = styled(P1)`
  flex-grow: 1;
  margin-top: 12px;
`;

const Topics = styled(P2)`
  color: var(--color-i-blue);
`;

const Title = styled(H3)`
  @media only screen and (max-width: 47.95em) {
    font-size: 18px;
  }
`;

type CardProps = {
  educator: {
    username: string;
    avatar: string;
    introPhoto: string;
    firstName: string;
    lastName: string;
    bio: string;
    topicsDisplay: string;
    levelInfo: {
      followerCount: number;
      levelId: number;
    };
  };
  dataAnalytics: string;
  index: number;
};

const Card: FC<CardProps> = ({ educator, dataAnalytics, index }) => {
  const {
    username,
    avatar,
    introPhoto,
    firstName,
    lastName,
    bio,
    topicsDisplay,
    levelInfo
  } = educator;
  const thumbnail = avatar || introPhoto;
  const dispatch = useDispatch();
  const isMobile = useIsMobile();

  const cardClickAnalytics = () => {};

  const { levelsDisplayInfo = [] } = useLevelDisplayInfo();

  const currentLevelDisplayInfo = levelsDisplayInfo.find(
    (levelDisplayInfo: { levelId: number }) => {
      return levelDisplayInfo.levelId === levelInfo?.levelId;
    }
  );

  return (
    <Link href="/[authorID]" as={`/@${username}`} onClick={cardClickAnalytics}>
      <CardContainer>
        <CardThumbnailWrapper>
          <CardThumbnail
            imgUrl={thumbnail}
            orientation="vertical"
            data-analytics={dataAnalytics}
            className="thumbnail"
          />
          {currentLevelDisplayInfo ? (
            <EducatorLevelContainer
              color={currentLevelDisplayInfo?.color?.secondary}
            >
              <EducatorLevelBadgeContainer>
                {isMobile ? (
                  <Image
                    src={currentLevelDisplayInfo?.asset?.badge}
                    width={15}
                    height={15}
                    alt="badge"
                  />
                ) : (
                  <Image
                    src={currentLevelDisplayInfo?.asset?.badge}
                    width={20}
                    height={20}
                    alt="badge"
                  />
                )}
              </EducatorLevelBadgeContainer>
            </EducatorLevelContainer>
          ) : null}
        </CardThumbnailWrapper>

        <CardWrapper>
          <TopContent>
            <Title>{`${firstName || ''} ${lastName || ''}`}</Title>
            <Description>{bio}</Description>
          </TopContent>
          <Topics>{topicsDisplay}</Topics>
        </CardWrapper>
      </CardContainer>
    </Link>
  );
};

type PageHeadProps = {
  block: {
    type: string;
    data: any[];
  };
  goalDetails?: {
    name: string;
    uid: string;
  };
  countryName: string;
  href?: string;
  as?: string;
};

const PageHead: FC<PageHeadProps> = ({
  block,
  countryName,
  href = null,
  as = null,
  goalDetails = {}
}) => {
  const { name, uid } = goalDetails;
  const { data: educators = [] } = block;
  const goalEvent = useGoalEventProps({ goalUID: uid });

  const seeAllClickEvent = () => {
    segment.track(SEE_ALL_CLICKED, {
      ...goalEvent,
      last_primary_source: GOAL_PAGE,
      last_primary_source_section: 'Prepare with Top Educators'
    });
  };

  return (
    <SecWrapper>
      <CardCarousel
        label="Prepare with Top Educators"
        subLabel={`Access to ${countryName}'s best ${name} educators is just a subscription away`}
        step={2}
        href={href}
        as={as}
        dataAnalytics="educators"
        type="educators"
        handleSeeAll={seeAllClickEvent}
      >
        {educators.map((educator, index) => (
          <EducatorDetailedCard
            key={educator.uid}
            index={index}
            gridColumn="span 20"
            educator={educator}
            showBadge
            dataAnalytics="subscription"
            className="carousel-wrapper-free-platform"
          />
        ))}
      </CardCarousel>
    </SecWrapper>
  );
};

export default PageHead;
